import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { motion } from 'framer-motion';
import { Calendar, CheckCircle2, Clock, CreditCard, Search, XCircle, Filter, User } from 'lucide-react';
import { toast } from 'sonner';

const statusStyles = {
  pending: 'bg-amber-100 text-amber-700',
  confirmed: 'bg-emerald-100 text-emerald-700',
  completed: 'bg-violet-100 text-violet-700',
  cancelled: 'bg-rose-100 text-rose-700'
};

const paymentStyles = {
  pending: 'bg-orange-100 text-orange-700',
  paid: 'bg-emerald-100 text-emerald-700'
};

const BookingsManagement = () => {
  const [bookings, setBookings] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [students, setStudents] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [paymentFilter, setPaymentFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [teacherFilter, setTeacherFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [updatingId, setUpdatingId] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const [bookingsRes, teachersRes] = await Promise.all([
        axios.get(`${API}/admin/bookings`, { headers }),
        axios.get(`${API}/teachers`, { headers })
      ]);
      const bookingsData = bookingsRes.data || [];
      setBookings(bookingsData);
      setTeachers(teachersRes.data || []);
      
      // Build student lookup from enriched booking data (student_name comes from backend)
      const studentLookup = {};
      bookingsData.forEach(b => {
        if (b.student_id) {
          studentLookup[b.student_id] = { 
            name: b.student_name || `Student ${b.student_id.slice(0, 6)}`, 
            email: b.student_email || '' 
          };
        }
      });
      
      setStudents(studentLookup);
    } catch (error) {
      toast.error('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  const teacherLookup = useMemo(() => {
    return teachers.reduce((acc, teacher) => {
      acc[teacher.id] = teacher;
      return acc;
    }, {});
  }, [teachers]);

  const filteredBookings = useMemo(() => {
    return bookings.filter((booking) => {
      const teacherName = teacherLookup[booking.teacher_id]?.name || '';
      const studentName = students[booking.student_id]?.name || booking.student_id;
      const searchTarget = `${studentName} ${booking.student_id} ${booking.teacher_id} ${teacherName} ${booking.subject}`.toLowerCase();
      const matchesSearch = searchTarget.includes(searchQuery.toLowerCase());
      const matchesPayment = paymentFilter === 'all' || booking.payment_status === paymentFilter;
      const matchesStatus = statusFilter === 'all' || booking.booking_status === statusFilter;
      const matchesTeacher = teacherFilter === 'all' || booking.teacher_id === teacherFilter;
      
      // Date range filter
      let matchesDate = true;
      if (dateFrom) {
        matchesDate = matchesDate && booking.booking_date >= dateFrom;
      }
      if (dateTo) {
        matchesDate = matchesDate && booking.booking_date <= dateTo;
      }
      
      return matchesSearch && matchesPayment && matchesStatus && matchesTeacher && matchesDate;
    });
  }, [bookings, teacherLookup, students, searchQuery, paymentFilter, statusFilter, teacherFilter, dateFrom, dateTo]);

  const totals = useMemo(() => {
    const pending = bookings.filter((b) => b.booking_status === 'pending').length;
    const confirmed = bookings.filter((b) => b.booking_status === 'confirmed').length;
    const completed = bookings.filter((b) => b.booking_status === 'completed').length;
    const paid = bookings.filter((b) => b.payment_status === 'paid').length;
    const totalRevenue = bookings.filter(b => b.payment_status === 'paid').reduce((sum, b) => sum + b.total_amount, 0);
    return { pending, confirmed, completed, paid, total: bookings.length, totalRevenue };
  }, [bookings]);

  const handleStatusUpdate = async (bookingId, newPaymentStatus, newBookingStatus) => {
    try {
      setUpdatingId(bookingId);
      const booking = bookings.find(b => b.id === bookingId);
      
      // If payment status changed, update payment
      if (newPaymentStatus && newPaymentStatus !== booking?.payment_status) {
        await axios.put(`${API}/bookings/${bookingId}/payment?status=${newPaymentStatus}`);
      }
      
      // If booking status changed, update status
      if (newBookingStatus && newBookingStatus !== booking?.booking_status) {
        await axios.put(`${API}/bookings/${bookingId}/status?status=${newBookingStatus}`);
      }
      
      toast.success('Booking updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update booking');
    } finally {
      setUpdatingId(null);
    }
  };

  const clearFilters = () => {
    setSearchQuery('');
    setPaymentFilter('all');
    setStatusFilter('all');
    setTeacherFilter('all');
    setDateFrom('');
    setDateTo('');
  };

  const hasActiveFilters = searchQuery || paymentFilter !== 'all' || statusFilter !== 'all' || teacherFilter !== 'all' || dateFrom || dateTo;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]" data-testid="admin-bookings-loading">
        <div className="text-lg text-slate-600">Loading bookings...</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="mb-8 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-2" data-testid="admin-bookings-title">
            Bookings Management
          </h1>
          <p className="text-lg text-slate-600" data-testid="admin-bookings-subtitle">
            Track session status, payments, and student requests
          </p>
        </div>
      </div>

      {/* Filters Section */}
      <Card className="border-0 shadow-lg mb-6">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-4">
            <Filter className="w-5 h-5 text-violet-600" />
            <span className="font-medium text-slate-700">Filters</span>
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="ml-auto text-slate-500 hover:text-slate-700">
                Clear all
              </Button>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-3">
            {/* Search */}
            <div className="relative lg:col-span-2">
              <Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search student, teacher, subject..."
                className="pl-9"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="admin-bookings-search"
              />
            </div>
            
            {/* Teacher Filter */}
            <select
              className="border border-slate-200 rounded-md px-3 py-2 text-sm bg-white"
              value={teacherFilter}
              onChange={(e) => setTeacherFilter(e.target.value)}
              data-testid="admin-bookings-teacher-filter"
            >
              <option value="all">All Teachers</option>
              {teachers.map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>

            {/* Payment Filter */}
            <select
              className="border border-slate-200 rounded-md px-3 py-2 text-sm bg-white"
              value={paymentFilter}
              onChange={(e) => setPaymentFilter(e.target.value)}
              data-testid="admin-bookings-payment-filter"
            >
              <option value="all">All Payments</option>
              <option value="pending">Pending</option>
              <option value="paid">Paid</option>
            </select>
            
            {/* Status Filter */}
            <select
              className="border border-slate-200 rounded-md px-3 py-2 text-sm bg-white"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              data-testid="admin-bookings-status-filter"
            >
              <option value="all">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="confirmed">Confirmed</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
            
            {/* Date Range - appears on new row on mobile */}
            <div className="lg:col-span-2 flex gap-2">
              <div className="flex-1">
                <Input
                  type="date"
                  placeholder="From"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="text-sm"
                  data-testid="admin-bookings-date-from"
                />
              </div>
              <div className="flex-1">
                <Input
                  type="date"
                  placeholder="To"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="text-sm"
                  data-testid="admin-bookings-date-to"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
        {[{
          label: 'Total',
          value: totals.total,
          icon: Calendar
        }, {
          label: 'Pending',
          value: totals.pending,
          icon: Clock
        }, {
          label: 'Confirmed',
          value: totals.confirmed,
          icon: CheckCircle2
        }, {
          label: 'Completed',
          value: totals.completed,
          icon: CheckCircle2
        }, {
          label: 'Paid',
          value: totals.paid,
          icon: CreditCard
        }, {
          label: 'Revenue',
          value: `₹${totals.totalRevenue.toLocaleString()}`,
          icon: CreditCard,
          isRevenue: true
        }].map((stat) => {
          const Icon = stat.icon;
          return (
            <motion.div key={stat.label} whileHover={{ scale: 1.02, y: -4 }}>
              <Card className="border-0 shadow-lg">
                <CardContent className="p-4 flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-500">{stat.label}</p>
                    <p className={`${stat.isRevenue ? 'text-lg' : 'text-2xl'} font-bold text-slate-900`} data-testid={`admin-bookings-stat-${stat.label.toLowerCase()}`}>
                      {stat.value}
                    </p>
                  </div>
                  <div className="w-9 h-9 rounded-xl bg-violet-100 flex items-center justify-center">
                    <Icon className="w-4 h-4 text-violet-600" />
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          );
        })}
      </div>

      {/* Bookings Table */}
      <Card className="border-0 shadow-xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-violet-600" />
            Bookings ({filteredBookings.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredBookings.length === 0 ? (
            <div className="py-12 text-center text-slate-600" data-testid="admin-bookings-empty">
              No bookings match your filters.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-sm text-slate-500">
                    <th className="py-3 px-3">Student</th>
                    <th className="py-3 px-3">Teacher</th>
                    <th className="py-3 px-3">Session</th>
                    <th className="py-3 px-3">Payment</th>
                    <th className="py-3 px-3">Status</th>
                    <th className="py-3 px-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredBookings.map((booking) => {
                    const teacher = teacherLookup[booking.teacher_id];
                    const student = students[booking.student_id];
                    return (
                      <tr
                        key={booking.id}
                        className="border-b border-slate-100 hover:bg-slate-50"
                        data-testid={`admin-booking-row-${booking.id}`}
                      >
                        <td className="py-3 px-3">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-violet-100 flex items-center justify-center">
                              <User className="w-4 h-4 text-violet-600" />
                            </div>
                            <div>
                              <div className="text-sm font-medium text-slate-900" data-testid={`admin-booking-student-${booking.id}`}>
                                {booking.student_name || student?.name || 'Student'}
                              </div>
                              <div className="text-xs text-slate-500">{booking.student_id.slice(0, 8)}...</div>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-3">
                          <div className="text-sm font-medium text-slate-900" data-testid={`admin-booking-teacher-${booking.id}`}>
                            {booking.teacher_name || teacher?.name || 'Teacher'}
                          </div>
                          <div className="text-xs text-slate-500">{booking.subject}</div>
                        </td>
                        <td className="py-3 px-3 text-sm text-slate-700">
                          <div>{new Date(booking.booking_date).toLocaleDateString()}</div>
                          <div className="text-xs text-slate-500">{booking.start_time} · {booking.duration_hours}h</div>
                        </td>
                        <td className="py-3 px-3">
                          <Badge className={`${paymentStyles[booking.payment_status] || 'bg-slate-100 text-slate-600'} border-0`} data-testid={`admin-booking-payment-${booking.id}`}>
                            {booking.payment_status}
                          </Badge>
                          <div className="text-xs text-slate-500 mt-1">₹{booking.total_amount.toFixed(2)}</div>
                        </td>
                        <td className="py-3 px-3">
                          <Badge className={`${statusStyles[booking.booking_status] || 'bg-slate-100 text-slate-600'} border-0`} data-testid={`admin-booking-status-${booking.id}`}>
                            {booking.booking_status}
                          </Badge>
                        </td>
                        <td className="py-3 px-3">
                          <div className="flex flex-wrap gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleStatusUpdate(booking.id, 'paid', booking.booking_status)}
                              disabled={booking.payment_status === 'paid' || updatingId === booking.id}
                              data-testid={`admin-booking-mark-paid-${booking.id}`}
                            >
                              <CheckCircle2 className="w-4 h-4 mr-1" />
                              Paid
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleStatusUpdate(booking.id, booking.payment_status, 'completed')}
                              disabled={booking.booking_status === 'completed' || updatingId === booking.id}
                              data-testid={`admin-booking-complete-${booking.id}`}
                            >
                              <CheckCircle2 className="w-4 h-4 mr-1" />
                              Done
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-rose-600 hover:bg-rose-50"
                              onClick={() => handleStatusUpdate(booking.id, booking.payment_status, 'cancelled')}
                              disabled={booking.booking_status === 'cancelled' || updatingId === booking.id}
                              data-testid={`admin-booking-cancel-${booking.id}`}
                            >
                              <XCircle className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default BookingsManagement;